import React, { useState, useEffect } from 'react';
import { get, post, put } from '../../utils/apiClient';
import { clearCacheKey } from '../../utils/cacheUtils';
import Base64Image from '../Base64Image';
import {
  AdminSectionTitle,
  AdminSectionText,
  AdminLoginLabel,
  AdminLoginInput,
  AdminLoginButton,
  AdminSaveButton,
} from '../../styles/AdminPageStyles';

const AdminAboutPage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const [uploadingOrgBio, setUploadingOrgBio] = useState(false);
  const [uploadingHistory, setUploadingHistory] = useState(false);
  const [uploadingFounder, setUploadingFounder] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [branchesText, setBranchesText] = useState('');
  const [departmentsText, setDepartmentsText] = useState('');

  useEffect(() => {
    const loadPage = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await get('/about');
        setPageData(data);
        const branchLines = Array.isArray(data.branches)
          ? data.branches
              .map((branch) => {
                if (!branch) return '';
                const parts = [];
                if (branch.name) parts.push(branch.name);
                if (branch.location || parts.length) parts.push(branch.location || '');
                if (typeof branch.isInternational === 'boolean') {
                  parts.push(branch.isInternational ? 'international' : '');
                }
                return parts.join(' | ').trim();
              })
              .filter((line) => line && line.length > 0)
          : [];
        setBranchesText(branchLines.join('\n'));

        const deptLines =
          data && data.churchStructure && Array.isArray(data.churchStructure.departments)
            ? data.churchStructure.departments
                .map((dept) => {
                  if (!dept) return '';
                  const parts = [];
                  if (dept.name) parts.push(dept.name);
                  if (dept.type) parts.push(dept.type);
                  if (dept.description) parts.push(dept.description);
                  return parts.join(' | ').trim();
                })
                .filter((line) => line && line.length > 0)
            : [];
        setDepartmentsText(deptLines.join('\n'));
      } catch (err) {
        setError(err && err.message ? err.message : 'Failed to load about page data.');
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, []);

  const handleHeaderTitleChange = (e) => {
    const { value } = e.target;
    setPageData((prev) => ({
      ...(prev || {}),
      header: {
        ...((prev && prev.header) || {}),
        title: value,
      },
    }));
  };

  const handleOrgBioTextChange = (e) => {
    const { value } = e.target;
    setPageData((prev) => ({
      ...(prev || {}),
      organizationBio: {
        ...((prev && prev.organizationBio) || {}),
        text: value,
      },
    }));
  };

  const handleSimpleFieldChange = (field, value) => {
    setPageData((prev) => ({
      ...(prev || {}),
      [field]: value,
    }));
  };

  const handleHistoryTextChange = (e) => {
    const { value } = e.target;
    setPageData((prev) => ({
      ...(prev || {}),
      history: {
        ...((prev && prev.history) || {}),
        text: value,
      },
    }));
  };

  const handleFounderFieldChange = (field, value) => {
    setPageData((prev) => ({
      ...(prev || {}),
      founder: {
        ...((prev && prev.founder) || {}),
        [field]: value,
      },
    }));
  };

  const handleGrowthStatChange = (field, value) => {
    setPageData((prev) => ({
      ...(prev || {}),
      growthStatistics: {
        ...((prev && prev.growthStatistics) || {}),
        [field]: value === '' ? undefined : Number(value),
      },
    }));
  };

  const handleHeaderBackgroundUpload = async (file) => {
    if (!file) return;
    setUploadingHeader(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('image', file);
      await post('/about/header/background-image', formData);
      const data = await get('/about');
      setPageData(data);
      clearCacheKey('about_data');
    } catch (err) {
      setUploadError(err && err.message ? err.message : 'Failed to upload header background.');
    } finally {
      setUploadingHeader(false);
    }
  };

  const handleOrgBioImageUpload = async (file) => {
    if (!file) return;
    setUploadingOrgBio(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('image', file);
      await post('/about/organization-bio/image', formData);
      const data = await get('/about');
      setPageData(data);
      clearCacheKey('about_data');
    } catch (err) {
      setUploadError(err && err.message ? err.message : 'Failed to upload organization image.');
    } finally {
      setUploadingOrgBio(false);
    }
  };

  const handleHistoryImageUpload = async (file) => {
    if (!file) return;
    setUploadingHistory(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('image', file);
      await post('/about/history/image', formData);
      const data = await get('/about');
      setPageData(data);
      clearCacheKey('about_data');
    } catch (err) {
      setUploadError(err && err.message ? err.message : 'Failed to upload history image.');
    } finally {
      setUploadingHistory(false);
    }
  };

  const handleFounderImageUpload = async (file) => {
    if (!file) return;
    setUploadingFounder(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('image', file);
      await post('/about/founder/image', formData);
      const data = await get('/about');
      setPageData(data);
      clearCacheKey('about_data');
    } catch (err) {
      setUploadError(err && err.message ? err.message : 'Failed to upload founder image.');
    } finally {
      setUploadingFounder(false);
    }
  };

  const parseBranchesList = (text) => {
    if (!text) return [];

    return text
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => {
        const parts = line.split('|').map((p) => p.trim());
        const [name = '', location = '', flag = ''] = parts;
        const lowerFlag = flag.toLowerCase();
        const isInternational = ['international', 'intl', 'int', 'yes', 'true', 'y'].includes(
          lowerFlag
        );
        return {
          name,
          location,
          isInternational,
        };
      });
  };

  const parseDepartmentsList = (text) => {
    if (!text) return [];

    return text
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => {
        const parts = line.split('|').map((p) => p.trim());
        const [name = '', type = '', description = ''] = parts;
        const result = {
          name,
          description,
        };
        if (type) {
          const normalizedType = type.toLowerCase();
          if (
            normalizedType === 'ministerial' ||
            normalizedType === 'administrative' ||
            normalizedType === 'functional'
          ) {
            result.type = normalizedType;
          }
        }
        return result;
      });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!pageData) return;

    setSaving(true);
    setError(null);
    setSaveSuccess(null);

    try {
      const branches = parseBranchesList(branchesText);

      const departments = parseDepartmentsList(departmentsText);

      const leadershipTeam = Array.isArray(pageData.leadershipTeam)
        ? pageData.leadershipTeam
        : [];

      const payload = {
        header: {
          ...(pageData.header || {}),
          title: (pageData.header && pageData.header.title) || 'About Us - Church',
        },
        organizationBio: pageData.organizationBio || {},
        mission: pageData.mission || '',
        vision: pageData.vision || '',
        coreBeliefs: pageData.coreBeliefs || '',
        motto: pageData.motto || '',
        history: pageData.history || {},
        founder: pageData.founder || {},
        headquartersAddress: pageData.headquartersAddress || '',
        branches,
        churchStructure: {
          departments,
        },
        growthStatistics: pageData.growthStatistics || {},
        leadershipTeam,
      };

      const updated = await put('/about', payload);
      setPageData(updated);
      clearCacheKey('about_data');
      setSaveSuccess('About page saved successfully.');
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to save about page content.');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !pageData) {
    return (
      <>
        <AdminSectionTitle>About Page CMS</AdminSectionTitle>
        <AdminSectionText>Loading about page data...</AdminSectionText>
      </>
    );
  }

  const header = pageData && pageData.header ? pageData.header : null;
  const organizationBio = pageData ? pageData.organizationBio : null;

  return (
    <>
      <AdminSectionTitle>About Page CMS</AdminSectionTitle>

      <form onSubmit={handleSave}>
        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="about-header-title">Header Title</AdminLoginLabel>
          <AdminLoginInput
            id="about-header-title"
            value={(header && header.title) || ''}
            onChange={handleHeaderTitleChange}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
            Header Background Image
          </AdminSectionTitle>
          {header && header.backgroundImage && (
            <div style={{ marginBottom: 8 }}>
              <Base64Image
                image={header.backgroundImage}
                alt={header.title || 'About header'}
                style={{ width: 260, borderRadius: 6 }}
              />
            </div>
          )}
          <AdminLoginLabel htmlFor="about-header-bg">Upload Header Background</AdminLoginLabel>
          <input
            id="about-header-bg"
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
              if (file) {
                handleHeaderBackgroundUpload(file);
                e.target.value = '';
              }
            }}
            style={{ fontSize: '0.9rem' }}
          />
          {uploadingHeader && (
            <AdminSectionText style={{ fontSize: '0.85rem' }}>
              Uploading header background...
            </AdminSectionText>
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
            Who We Are
          </AdminSectionTitle>
          <AdminLoginLabel htmlFor="about-org-bio-text">Organization Bio Text</AdminLoginLabel>
          <textarea
            id="about-org-bio-text"
            rows={3}
            value={(organizationBio && organizationBio.text) || ''}
            onChange={handleOrgBioTextChange}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
          {organizationBio && organizationBio.image && (
            <div style={{ marginTop: 8, marginBottom: 8 }}>
              <Base64Image
                image={organizationBio.image}
                alt="Organization"
                style={{ width: 220, borderRadius: 6 }}
              />
            </div>
          )}
          <div style={{ marginTop: 8 }}>
            <AdminLoginLabel htmlFor="about-org-bio-image">
              Upload Organization Image
            </AdminLoginLabel>
            <input
              id="about-org-bio-image"
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                if (file) {
                  handleOrgBioImageUpload(file);
                  e.target.value = '';
                }
              }}
              style={{ fontSize: '0.9rem' }}
            />
            {uploadingOrgBio && (
              <AdminSectionText style={{ fontSize: '0.85rem' }}>
                Uploading organization image...
              </AdminSectionText>
            )}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
            Mission, Vision & Beliefs
          </AdminSectionTitle>
          <AdminLoginLabel htmlFor="about-mission">Mission</AdminLoginLabel>
          <textarea
            id="about-mission"
            rows={2}
            value={pageData.mission || ''}
            onChange={(e) => handleSimpleFieldChange('mission', e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem', marginBottom: 8 }}
          />
          <AdminLoginLabel htmlFor="about-vision">Vision</AdminLoginLabel>
          <textarea
            id="about-vision"
            rows={2}
            value={pageData.vision || ''}
            onChange={(e) => handleSimpleFieldChange('vision', e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem', marginBottom: 8 }}
          />
          <AdminLoginLabel htmlFor="about-core-beliefs">Core Beliefs</AdminLoginLabel>
          <textarea
            id="about-core-beliefs"
            rows={3}
            value={pageData.coreBeliefs || ''}
            onChange={(e) => handleSimpleFieldChange('coreBeliefs', e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem', marginBottom: 8 }}
          />
          <AdminLoginLabel htmlFor="about-motto">Motto</AdminLoginLabel>
          <AdminLoginInput
            id="about-motto"
            value={pageData.motto || ''}
            onChange={(e) => handleSimpleFieldChange('motto', e.target.value)}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
            Our History
          </AdminSectionTitle>
          <AdminLoginLabel htmlFor="about-history-text">History Text</AdminLoginLabel>
          <textarea
            id="about-history-text"
            rows={3}
            value={(pageData.history && pageData.history.text) || ''}
            onChange={handleHistoryTextChange}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
          {pageData.history && pageData.history.image && (
            <div style={{ marginTop: 8, marginBottom: 8 }}>
              <Base64Image
                image={pageData.history.image}
                alt="History"
                style={{ width: 220, borderRadius: 6 }}
              />
            </div>
          )}
          <div style={{ marginTop: 8 }}>
            <AdminLoginLabel htmlFor="about-history-image">
              Upload History Image
            </AdminLoginLabel>
            <input
              id="about-history-image"
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                if (file) {
                  handleHistoryImageUpload(file);
                  e.target.value = '';
                }
              }}
              style={{ fontSize: '0.9rem' }}
            />
            {uploadingHistory && (
              <AdminSectionText style={{ fontSize: '0.85rem' }}>
                Uploading history image...
              </AdminSectionText>
            )}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
            Founder
          </AdminSectionTitle>
          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="about-founder-name">Name</AdminLoginLabel>
            <AdminLoginInput
              id="about-founder-name"
              value={(pageData.founder && pageData.founder.name) || ''}
              onChange={(e) => handleFounderFieldChange('name', e.target.value)}
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="about-founder-title">Title</AdminLoginLabel>
            <AdminLoginInput
              id="about-founder-title"
              value={(pageData.founder && pageData.founder.title) || ''}
              onChange={(e) => handleFounderFieldChange('title', e.target.value)}
            />
          </div>
          <div>
            <AdminLoginLabel htmlFor="about-founder-bio">Bio</AdminLoginLabel>
            <textarea
              id="about-founder-bio"
              rows={3}
              value={(pageData.founder && pageData.founder.bio) || ''}
              onChange={(e) => handleFounderFieldChange('bio', e.target.value)}
              style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
            />
          </div>
          {pageData.founder && pageData.founder.image && (
            <div style={{ marginTop: 8, marginBottom: 8 }}>
              <Base64Image
                image={pageData.founder.image}
                alt={pageData.founder.name || 'Founder'}
                style={{ width: 200, borderRadius: 6 }}
              />
            </div>
          )}
          <div style={{ marginTop: 8 }}>
            <AdminLoginLabel htmlFor="about-founder-image">
              Upload Founder Image
            </AdminLoginLabel>
            <input
              id="about-founder-image"
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                if (file) {
                  handleFounderImageUpload(file);
                  e.target.value = '';
                }
              }}
              style={{ fontSize: '0.9rem' }}
            />
            {uploadingFounder && (
              <AdminSectionText style={{ fontSize: '0.85rem' }}>
                Uploading founder image...
              </AdminSectionText>
            )}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
            Locations & Growth
          </AdminSectionTitle>
          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="about-hq-address">Headquarters Address</AdminLoginLabel>
            <textarea
              id="about-hq-address"
              rows={2}
              value={pageData.headquartersAddress || ''}
              onChange={(e) => handleSimpleFieldChange('headquartersAddress', e.target.value)}
              style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
            />
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 160px' }}>
              <AdminLoginLabel htmlFor="about-members-count">Members Count</AdminLoginLabel>
              <AdminLoginInput
                id="about-members-count"
                type="number"
                value={
                  pageData.growthStatistics && typeof pageData.growthStatistics.membersCount === 'number'
                    ? pageData.growthStatistics.membersCount
                    : ''
                }
                onChange={(e) => handleGrowthStatChange('membersCount', e.target.value)}
              />
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <AdminLoginLabel htmlFor="about-branches-count">Branches Count</AdminLoginLabel>
              <AdminLoginInput
                id="about-branches-count"
                type="number"
                value={
                  pageData.growthStatistics && typeof pageData.growthStatistics.branchesCount === 'number'
                    ? pageData.growthStatistics.branchesCount
                    : ''
                }
                onChange={(e) => handleGrowthStatChange('branchesCount', e.target.value)}
              />
            </div>
            <div style={{ flex: '1 1 180px' }}>
              <AdminLoginLabel htmlFor="about-intl-branches-count">International Branches</AdminLoginLabel>
              <AdminLoginInput
                id="about-intl-branches-count"
                type="number"
                value={
                  pageData.growthStatistics &&
                  typeof pageData.growthStatistics.internationalBranchesCount === 'number'
                    ? pageData.growthStatistics.internationalBranchesCount
                    : ''
                }
                onChange={(e) => handleGrowthStatChange('internationalBranchesCount', e.target.value)}
              />
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <AdminLoginLabel htmlFor="about-founded-year">Founded Year</AdminLoginLabel>
              <AdminLoginInput
                id="about-founded-year"
                type="number"
                value={
                  pageData.growthStatistics && typeof pageData.growthStatistics.foundedYear === 'number'
                    ? pageData.growthStatistics.foundedYear
                    : ''
                }
                onChange={(e) => handleGrowthStatChange('foundedYear', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
            Branches
          </AdminSectionTitle>
          <AdminLoginLabel htmlFor="about-branches-list">
            One branch per line. Format: Name | Location | international (optional)
          </AdminLoginLabel>
          <textarea
            id="about-branches-list"
            rows={Math.max(4, branchesText.split('\n').length || 1)}
            value={branchesText}
            onChange={(e) => setBranchesText(e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
          <AdminSectionText style={{ fontSize: '0.8rem', marginTop: 4 }}>
            Example: VFM Central Church | Monrovia, Liberia | international
          </AdminSectionText>
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
            Departments
          </AdminSectionTitle>
          <AdminLoginLabel htmlFor="about-departments-list">
            One department per line. Format: Name | type | description (type is optional;
            ministerial / administrative / functional)
          </AdminLoginLabel>
          <textarea
            id="about-departments-list"
            rows={Math.max(4, departmentsText.split('\n').length || 1)}
            value={departmentsText}
            onChange={(e) => setDepartmentsText(e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
          <AdminSectionText style={{ fontSize: '0.8rem', marginTop: 4 }}>
            Example: Youth Department | functional | Oversees youth programs
          </AdminSectionText>
        </div>

        <div style={{ marginTop: 32, display: 'flex', alignItems: 'center', gap: '16px' }}>
          <AdminSaveButton type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save About Page'}
          </AdminSaveButton>
          {saveSuccess && (
            <span style={{ color: '#1b5e20', fontWeight: '500' }}>{saveSuccess}</span>
          )}
          {error && (
            <span style={{ color: '#b71c1c', fontWeight: '500' }}>{error}</span>
          )}
          {uploadError && (
            <span style={{ color: '#b71c1c', fontWeight: '500' }}>{uploadError}</span>
          )}
        </div>
      </form>
    </>
  );
};

export default AdminAboutPage;
